import React from 'react';
import SolutionPageTemplate from './SolutionPageTemplate';

const SETUP_CODE = `# 1. Enable the addon for your account ($50/mo)
zibby deploy --dedicated-ip enable

# Output:
#   Provisioning your dedicated egress IP...
#   Done in 8s.
#
#   Your static IP: 54.66.241.180
#
#   Add this to your firewall allowlist, then opt projects in:
#     zibby deploy --dedicated-ip use --project <project-id>

# 2. Opt each project in (free)
zibby deploy --dedicated-ip use --project my-project`;

const FirewalledReposPage = () => {
  return (
    <SolutionPageTemplate
      meta={{
        title: 'Coding agents behind your firewall — Zibby',
        description: 'Static outbound IP for whitelisting on self-hosted GitLab, GitHub Enterprise, Bitbucket, Salesforce. $50/mo dedicated egress addon.',
      }}
      eyebrow="FOR TEAMS"
      title="Coding agents that work behind your firewall."
      lede="Self-hosted GitLab, GitHub Enterprise, Bitbucket Data Center, Salesforce, Oracle Cloud — anything with an IP allowlist. Pin all your agent's outbound traffic to one static IP your network team whitelists once."
      ctaPrimary={{ label: 'Talk to sales', href: 'mailto:support@zibby.dev?subject=Dedicated egress IP — Enterprise' }}
      ctaSecondary={{ label: 'Read the docs →', href: 'https://docs.zibby.app/cloud/dedicated-egress', external: true }}
      sections={[
        {
          title: 'The default doesn\'t work for enterprise.',
          body: (
            <>
              <p>
                By default, every Zibby agent exits via a random AWS IP that changes every run. For most customers that's fine. But if your code lives behind a corporate VPN — self-hosted GitLab, GitHub Enterprise, Salesforce CRM data, internal APIs — the random-IP behavior makes Zibby unusable.
              </p>
              <p>
                Your network team isn't going to whitelist a CIDR block of "everything AWS could possibly assign us." They'll whitelist <strong>one IP, once.</strong>
              </p>
            </>
          ),
        },
        {
          title: 'Your own static IP. Whitelist it once. Forever.',
          body: (
            <>
              <p>
                $50/mo per account. Account-wide. All your projects share the same IP. Every outbound HTTPS call from your agent — git clone, npm install from a private registry, fetch() to a customer API, curl in a shell script — exits via that IP.
              </p>
              <p><strong>What is and isn't proxied:</strong></p>
              <ul>
                <li>✓ <code>fetch()</code> from agent code → via static IP</li>
                <li>✓ <code>git clone</code> private repos → via static IP</li>
                <li>✓ <code>npm install</code> from private registries → via static IP</li>
                <li>✓ <code>curl</code> / <code>wget</code> in shell scripts → via static IP</li>
                <li>— AWS SDK calls (S3, DynamoDB) → direct (AWS doesn't allowlist)</li>
              </ul>
            </>
          ),
          code: SETUP_CODE,
        },
        {
          title: 'Real-world allowlisting.',
          body: (
            <>
              <p><strong>GitLab self-hosted:</strong></p>
              <pre style={{ background: '#0d0d0d', padding: '12px 16px', borderRadius: 6, fontFamily: 'SF Mono, Menlo, monospace', fontSize: 13, margin: '6px 0 14px', color: 'rgba(255,255,255,0.86)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{`Admin → Settings → Network → Outbound requests
→ Allowlist: <your-zibby-ip>`}
              </pre>

              <p><strong>GitHub Enterprise:</strong></p>
              <pre style={{ background: '#0d0d0d', padding: '12px 16px', borderRadius: 6, fontFamily: 'SF Mono, Menlo, monospace', fontSize: 13, margin: '6px 0 14px', color: 'rgba(255,255,255,0.86)', whiteSpace: 'pre-wrap' }}>
{`Site admin → Authentication → IP allowlist
→ Add: <your-zibby-ip>`}
              </pre>

              <p><strong>AWS Security Group (e.g. private RDS / internal ECS):</strong></p>
              <pre style={{ background: '#0d0d0d', padding: '12px 16px', borderRadius: 6, fontFamily: 'SF Mono, Menlo, monospace', fontSize: 13, margin: '6px 0 14px', color: 'rgba(255,255,255,0.86)', whiteSpace: 'pre-wrap' }}>
{`Inbound rule → HTTPS → Source: <your-zibby-ip>/32`}
              </pre>

              <p><strong>Salesforce:</strong></p>
              <pre style={{ background: '#0d0d0d', padding: '12px 16px', borderRadius: 6, fontFamily: 'SF Mono, Menlo, monospace', fontSize: 13, margin: '6px 0 14px', color: 'rgba(255,255,255,0.86)', whiteSpace: 'pre-wrap' }}>
{`Setup → Network Access → Trusted IP ranges
→ <your-zibby-ip> to <your-zibby-ip>`}
              </pre>
            </>
          ),
        },
        {
          title: 'What stays the same.',
          body: (
            <>
              <p>
                Your IP is <strong>stable as long as you have the addon enabled</strong>:
              </p>
              <ul>
                <li>Survives Zibby infrastructure changes (we replace proxy boxes regularly)</li>
                <li>Survives our region failovers</li>
                <li>Survives your own agent redeploys</li>
              </ul>
              <p>
                The only way it changes is if <em>you</em> disable the addon. Re-enabling later gives you a different IP — re-whitelist on your destination services.
              </p>
            </>
          ),
        },
      ]}
      cta={{
        title: 'Bring AI coding agents inside your firewall.',
        body: 'Email us with your stack — we\'ll confirm whether your network setup works with our static-IP addon (it almost always does).',
        primary: { label: 'Talk to sales', href: 'mailto:support@zibby.dev?subject=Dedicated egress IP — Enterprise' },
        secondary: { label: 'Read the docs', href: 'https://docs.zibby.app/cloud/dedicated-egress', external: true },
      }}
    />
  );
};

export default FirewalledReposPage;
